import {useCallback, useState, useMemo, useEffect} from "react";
import {Paper, Button, Modal, TextInput, Select, Switch, SegmentedControl, ColorInput, ActionIcon} from "@mantine/core";
import {DownloadIcon, PlusIcon, TrashIcon} from "@phosphor-icons/react";
import {Col, Row} from "../../components/Flex/Flex";
import SplitButton from "../../components/SplitButton/SplitButton.tsx";
import GalaxyMap from "./GalaxyMap.tsx";
import {SelectedSystem, CustomMarker, SelectedFaction, MapMode} from "./types";
import {useSystems, useSectors, useFactions} from "../../utils/api";
import {useParams} from "react-router";
import useSavedItem from "../../components/SharedSave/useSavedItem.ts";
import SharedSave from "../../components/SharedSave/SharedSave.tsx";
// @ts-ignore
import style from './Map.module.css';
import {track} from "../../utils/analytics.ts";

interface SystemRowProps {
  system: SelectedSystem,
  onChangeColor: (id: string, color: string) => void,
  onRemove: (id: string) => void
}

interface FactionRowProps {
  faction: SelectedFaction,
  onChangeColor: (id: string, color: string) => void,
  onRemove: (id: string) => void
}

export default function InteractiveMap() {
  const {id} = useParams();
  const savedData = useSavedItem('map', id);
  const {systems, isLoading: systemsLoading} = useSystems();
  const {sectors, isLoading: sectorsLoading} = useSectors();
  const {factions} = useFactions();

  const [mode, setMode] = useState<MapMode>('standard');
  const [selectedSystems, setSelectedSystems] = useState<SelectedSystem[]>([]);
  const [showLabels, setShowLabels] = useState<boolean>(true);
  const [customMarkers, setCustomMarkers] = useState<CustomMarker[]>([]);
  const [selectedFactions, setSelectedFactions] = useState<SelectedFaction[]>([]);
  const [backgroundColor, setBackgroundColor] = useState<string>('#000000');
  const [hideNonHighlighted, setHideNonHighlighted] = useState<boolean>(false);
  const [showLegendInExport, setShowLegendInExport] = useState<boolean>(true);

  useEffect(() => {
    if (!savedData?.content) {
      return;
    }
    setMode(savedData.content.mode ?? 'standard');
    setSelectedSystems(savedData.content.selectedSystems ?? []);
    setShowLabels(savedData.content.showLabels ?? true);
    setCustomMarkers(savedData.content.customMarkers ?? []);
    setSelectedFactions(savedData.content.selectedFactions ?? []);
    setBackgroundColor(savedData.content.backgroundColor ?? '#000000');
    setHideNonHighlighted(savedData.content.hideNonHighlighted ?? false);
    setShowLegendInExport(savedData.content.showLegendInExport ?? true);
  }, [savedData?.id]);

  const currentData = useMemo(() => ({
    mode,
    selectedSystems,
    showLabels,
    customMarkers,
    selectedFactions,
    backgroundColor,
    hideNonHighlighted,
    showLegendInExport
  }), [mode, selectedSystems, showLabels, customMarkers, selectedFactions, backgroundColor, hideNonHighlighted, showLegendInExport]);

  // Custom marker dialog state
  const [showMarkerDialog, setShowMarkerDialog] = useState(false);
  const [markerX, setMarkerX] = useState<string>('');
  const [markerY, setMarkerY] = useState<string>('');
  const [markerLabel, setMarkerLabel] = useState<string>('');
  const [markerColor, setMarkerColor] = useState<string>('#ff0000');

  const sortedSystems = useMemo(() => systems.slice().sort((a, b) => a.name.localeCompare(b.name)), [systems]);

  const systemSelectData = useMemo(() => {
    return sortedSystems.map(s => ({
      value: s.id,
      label: s.name,
      disabled: !!selectedSystems.find(sel => sel.id === s.id),
    }));
  }, [sortedSystems, selectedSystems]);

  const availableFactions = useMemo(() => {
    if (systems.length === 0) return [];
    const governmentIds = [...new Set(systems.map(s => s.government).filter(Boolean))];
    const controllingFactions = factions.filter(f => governmentIds.includes(f.id));
    return controllingFactions.sort((a, b) => a.name.localeCompare(b.name));
  }, [systems, factions])

  const factionSelectData = useMemo(() => {
    return availableFactions.map(f => ({
      value: f.id,
      label: f.name,
      disabled: !!selectedFactions.find(sel => sel.id === f.id),
    }));
  }, [availableFactions, selectedFactions]);

  const onChangeSystemColor = useCallback((id: string, color: string) => {
    setSelectedSystems(prev => prev.map(s => {
      if (s.id === id) {
        return {...s, color};
      }
      return s;
    }))
  }, []);

  const onRemoveSystem = useCallback((id: string) => {
    setSelectedSystems(prev => prev.filter(s => s.id !== id));
  }, []);

  const onChangeFactionColor = useCallback((id: string, color: string) => {
    setSelectedFactions(prev => prev.map(f => {
      if (f.id === id) {
        return {...f, color};
      }
      return f;
    }))
  }, []);

  const onRemoveFaction = useCallback((id: string) => {
    setSelectedFactions(prev => prev.filter(f => f.id !== id));
  }, []);

  const addCustomMarker = () => {
    const x = parseFloat(markerX);
    const y = parseFloat(markerY);

    if (isNaN(x) || isNaN(y) || !markerLabel.trim()) {
      alert('Please enter valid coordinates and a label');
      return;
    }

    const newMarker: CustomMarker = {
      id: Date.now().toString(),
      x,
      y,
      label: markerLabel.trim(),
      color: markerColor
    };

    setCustomMarkers(prev => [...prev, newMarker]);
    track('map:marker-add');
    setShowMarkerDialog(false);
    setMarkerX('');
    setMarkerY('');
    setMarkerLabel('');
    setMarkerColor('#ff0000');
  };

  const onChangeMarkerColor = useCallback((id: string, color: string) => {
    setCustomMarkers(prev => prev.map(m => {
      if (m.id === id) {
        return {...m, color};
      }
      return m;
    }))
  }, []);

  const removeCustomMarker = (id: string) => {
    setCustomMarkers(prev => prev.filter(m => m.id !== id));
  };

  const exportMapAsPNG = (resolution: number) => {
    const sourceCanvas = document.querySelector('canvas');
    if (!sourceCanvas) {
      alert('Canvas not found');
      return;
    }

    track('map:export-png', {resolution});
    const scale = resolution / sourceCanvas.width;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = resolution;
    exportCanvas.height = resolution;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      alert('Failed to create export canvas');
      return;
    }

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

    ctx.scale(scale, scale);
    ctx.drawImage(sourceCanvas, 0, 0);

    ctx.setTransform(1, 0, 0, 1, 0, 0);

    if (mode === 'political' && selectedFactions.length > 0 && showLegendInExport) {
      const padding = 20;
      const lineHeight = 30;
      const boxSize = 20;
      const legendWidth = 250;
      const legendHeight = padding * 2 + lineHeight * selectedFactions.length;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(padding, padding, legendWidth, legendHeight);

      selectedFactions.forEach((faction, index) => {
        const y = padding + padding / 2 + index * lineHeight;
        const x = padding + padding / 2;

        ctx.fillStyle = faction.color;
        ctx.fillRect(x, y, boxSize, boxSize);

        ctx.fillStyle = '#ffffff';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(faction.name, x + boxSize + 10, y + boxSize / 2 + 6);
      });
    }

    exportCanvas.toBlob((blob) => {
      if (!blob) {
        alert('Failed to export image');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `galaxy-map-${resolution}px-${Date.now()}.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const modeOptions = [
    {label: 'Standard', value: 'standard'},
    {label: 'Political', value: 'political'}
  ];

  if (systemsLoading || sectorsLoading) {
    return <div>Loading map data...</div>;
  }

  return (
    <div style={{marginLeft: 20, marginRight: 20}}>
      <title>Interactive Galaxy Map</title>
      <Row wrap={false} align="stretch" mobileCol={true}>
        <Col className={style.wrapCol}>
          <GalaxyMap
            systems={systems}
            selected={selectedSystems}
            showLabels={showLabels}
            sectors={sectors}
            customMarkers={customMarkers}
            selectedFactions={selectedFactions}
            mode={mode}
            backgroundColor={backgroundColor}
            hideNonHighlighted={hideNonHighlighted}
          />
        </Col>

        <Paper shadow="xs" p="md" withBorder style={{order: 1, flexGrow: 1}}>
          <Row>
            <Col>
              <SegmentedControl
                value={mode}
                onChange={(val) => {
                  setMode(val as MapMode);
                  track('map:mode-change', {mode: val});
                }}
                data={modeOptions}
              />
            </Col>
            <Col grow={1}/>
            <Col>
              <SharedSave type="map" content={currentData} savedData={savedData} itemPath="/map"/>
            </Col>
            <Col>
              <SplitButton
                leftSection={<DownloadIcon size={16}/>}
                onClick={() => exportMapAsPNG(1024)}
                options={[
                  {label: "512px", onClick: () => exportMapAsPNG(512)},
                  {label: "1024px", onClick: () => exportMapAsPNG(1024)},
                  {label: "1536px", onClick: () => exportMapAsPNG(1536)},
                  {label: "2048px", onClick: () => exportMapAsPNG(2048)}
                ]}
                style={{marginBottom: '0.5rem'}}
                fullWidth
                menuTriggerLabel="Choose export resolution"
              >
                Export as PNG (1024px)
              </SplitButton>
            </Col>
          </Row>

          <Row>
            <Col>
              <ColorInput
                label="Background Color"
                value={backgroundColor}
                onChange={setBackgroundColor}
              />
            </Col>
            <Col>
              <Switch
                label="Hide non-highlighted systems"
                checked={hideNonHighlighted}
                onChange={e => setHideNonHighlighted(e.currentTarget.checked)}
                style={{marginTop: '1.5rem'}}
              />
            </Col>
            {mode === 'standard' && (
              <Col>
                <Switch
                  label="Show labels"
                  checked={showLabels}
                  onChange={e => setShowLabels(e.currentTarget.checked)}
                  style={{marginTop: '1.5rem'}}
                />
              </Col>)}
            {mode === 'political' && (
              <Col>
                <Switch
                  label="Show legend in export"
                  checked={showLegendInExport}
                  onChange={e => setShowLegendInExport(e.currentTarget.checked)}
                  style={{marginTop: '1.5rem'}}
                />
              </Col>
            )}
          </Row>

          <hr/>

          {mode === 'standard' && (
            <>
              <Row>
                <Col>
                  <Select
                    value={null}
                    data={systemSelectData}
                    onChange={(val) => {
                      if (!val) return;
                      const sys = systems.find(s => s.id === val);
                      if (sys) {
                        setSelectedSystems(prev => [
                          ...prev,
                          {...sys, color: '#ff0000'}
                        ]);
                      }
                    }}
                    searchable
                    clearable
                    placeholder="Search System"
                    style={{marginBottom: '0.5rem', width: '100%'}}
                  />
                </Col>
                <Col>
                  <Button
                    leftSection={<PlusIcon size={16} />}
                    onClick={() => setShowMarkerDialog(true)}
                    style={{marginBottom: '0.5rem', width: '100%'}}
                  >
                    Add Custom Marker
                  </Button>
                </Col>
              </Row>
              <hr/>
              <h4>Selected Systems:</h4>
              {selectedSystems.map(s => <SystemRow key={s.id} system={s} onChangeColor={onChangeSystemColor} onRemove={onRemoveSystem}/>)}

              {customMarkers.length > 0 && (
                <>
                  <h4>Custom Markers:</h4>
                  {customMarkers.map(m => (
                    <Row key={m.id} align="center">
                      <Col>
                        <ActionIcon variant="subtle" onClick={() => removeCustomMarker(m.id)}>
                          <TrashIcon size={16} />
                        </ActionIcon>
                      </Col>
                      <Col grow={1} style={{color: m.color}}>
                        {m.label} ({m.x}, {m.y})
                      </Col>
                      <Col>
                        <ColorInput value={m.color} onChange={val => onChangeMarkerColor(m.id, val)} size="xs" />
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </>
          )}

          {mode === 'political' && (
            <>
              <Select
                value={null}
                data={factionSelectData}
                onChange={(val) => {
                  if (!val) return;
                  const fac = availableFactions.find(f => (f.id) === val);
                  if (fac) {
                    setSelectedFactions(prev => [
                      ...prev,
                      {
                        id: fac.id,
                        name: fac.name,
                        color: '#ff0000',
                      }
                    ]);
                  }
                }}
                searchable
                clearable
                placeholder="Select Faction"
                style={{marginBottom: '0.5rem', width: '100%'}}
              />

              <hr/>
              <h4>Selected Factions:</h4>
              {selectedFactions.map(f => <FactionRow key={f.id} faction={f} onChangeColor={onChangeFactionColor} onRemove={onRemoveFaction}/>)}
            </>
          )}
        </Paper>
      </Row>

      <Modal
        title="Add Custom Marker"
        opened={showMarkerDialog}
        onClose={() => setShowMarkerDialog(false)}
        size="sm"
      >
        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
          <TextInput
            label="X Coordinate"
            value={markerX}
            onChange={(e) => setMarkerX(e.currentTarget.value)}
            placeholder="e.g., 100"
          />
          <TextInput
            label="Y Coordinate"
            value={markerY}
            onChange={(e) => setMarkerY(e.currentTarget.value)}
            placeholder="e.g., -50"
          />
          <TextInput
            label="Label"
            value={markerLabel}
            onChange={(e) => setMarkerLabel(e.currentTarget.value)}
            placeholder="e.g., Meeting Point"
          />
          <ColorInput
            label="Color"
            value={markerColor}
            onChange={setMarkerColor}
          />
          <Button onClick={addCustomMarker}>Add Marker</Button>
        </div>
      </Modal>
    </div>
  )
}

const SystemRow = ({system, onChangeColor, onRemove}: SystemRowProps) => {
  return (
    <Row align="center">
      <Col>
        <ActionIcon variant="subtle" onClick={() => onRemove(system.id)}>
          <TrashIcon size={16} />
        </ActionIcon>
      </Col>
      <Col grow={1} style={{color: system.color}}>
        {system.name} ({system.galX}, {system.galY})
      </Col>
      <Col>
        <ColorInput value={system.color} onChange={val => onChangeColor(system.id, val)} size="xs" />
      </Col>
    </Row>
  )
}

const FactionRow = ({faction, onChangeColor, onRemove}: FactionRowProps) => {
  return (
    <Row align="center">
      <Col>
        <ActionIcon variant="subtle" onClick={() => onRemove(faction.id)}>
          <TrashIcon size={16} />
        </ActionIcon>
      </Col>
      <Col grow={1} style={{color: faction.color}}>
        {faction.name}
      </Col>
      <Col>
        <ColorInput value={faction.color} onChange={val => onChangeColor(faction.id, val)} size="xs" />
      </Col>
    </Row>
  )
}
